import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { useParams, useNavigate } from 'react-router-dom';
import {
  CheckCircleIcon,
  XCircleIcon,
  CloudIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';

import apiClient from '../../services/apiClient';
import { getApiBaseUrl } from '../../utils/apiBaseUrl';

import { SharePointCredentials, WizardComponentProps } from '../../types';

interface CustomerSharePointCredentialsFormProps extends WizardComponentProps {
  customerId: string;
  onClose?: () => void;
  onFailure?: (error: string) => void;
}

const CustomerSharePointCredentialsForm: React.FC<CustomerSharePointCredentialsFormProps> = ({ customerId, onClose, isEmbedded, onFailure }) => {
  const queryClient = useQueryClient();
  const [showSecrets, setShowSecrets] = useState(false);
  const { phaseId } = useParams<{ phaseId: string }>();
  const navigate = useNavigate();

  const [formData, setFormData] = useState<SharePointCredentials>({
    siteUrl: '',
    clientId: '',
    tenantId: ''
  });

  const [testResult, setTestResult] = useState<{
    success?: boolean;
    message?: string;
    timestamp?: string;
  } | null>(null);

  // Fetch existing SharePoint credentials
  const { data: existingCredentials, isLoading } = useQuery({
    queryKey: ['customer-sharepoint-credentials', customerId],
    queryFn: () => apiClient.getCustomerSharePointCredentials(customerId),
    enabled: !!customerId,
    retry: false
  });

  useEffect(() => {
    if (existingCredentials) {
      setFormData({
        siteUrl: existingCredentials.siteUrl || '',
        clientId: existingCredentials.clientId || '',
        clientSecret: existingCredentials.clientSecret || '',
        tenantId: existingCredentials.tenantId || ''
      });
    }
  }, [existingCredentials]);

  // Check for OAuth callback status
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const spStatus = urlParams.get('sp');
    const spError = urlParams.get('oauth_error');

    if (spStatus === 'connected') {
      toast.success('SharePoint connected successfully!');
      setTestResult({
        success: true,
        message: 'SharePoint connected successfully (via OAuth)',
        timestamp: new Date().toISOString()
      });
      // Clear param
      urlParams.delete('sp');
      const newSearch = urlParams.toString();
      window.history.replaceState({}, '', `${window.location.pathname}${newSearch ? '?' + newSearch : ''}`);
    } else if (spError) {
      const errorMessage = `SharePoint connection failed: ${decodeURIComponent(spError)}`;

      // Clear param immediately to avoid loops
      urlParams.delete('oauth_error');
      const newSearch = urlParams.toString();
      window.history.replaceState({}, '', `${window.location.pathname}${newSearch ? '?' + newSearch : ''}`);

      if (onFailure) {
        onFailure(errorMessage);
      } else {
        toast.error(errorMessage);
        // Redirect to last functioning screen (Phase Dashboard or Customers)
        if (customerId && phaseId) {
          navigate(`/customers/${customerId}/phases/${phaseId}`);
        } else if (customerId) {
          navigate(`/customers/${customerId}`);
        } else {
          navigate('/dashboard');
        }
      }
    }
  }, [onFailure, customerId, phaseId, navigate]);

  // Save SharePoint credentials mutation
  const saveCredentialsMutation = useMutation({
    mutationFn: (data: SharePointCredentials) => apiClient.saveCustomerSharePointCredentials(customerId, data),
    onSuccess: () => {
      toast.success('SharePoint credentials saved successfully');
      queryClient.invalidateQueries({ queryKey: ['customer-sharepoint-credentials', customerId] });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to save SharePoint credentials');
    }
  });

  // Test SharePoint connection mutation
  const testConnectionMutation = useMutation({
    mutationFn: (data: any) => apiClient.testCustomerSharePointCredentials(customerId, data),
    onSuccess: (result) => {
      setTestResult(result);
      if (result.success) {
        if (result.authorizeUrl) {
          // OAuth flow initiated - redirect to authorization URL
          toast.success('Redirecting to Microsoft for authorization...');
          window.location.href = result.authorizeUrl;
        } else {
          toast.success('SharePoint connection test successful');
        }
      } else {
        const msg = result.message || 'SharePoint connection test failed';
        if (onFailure) {
          onFailure(msg);
        } else {
          toast.error(msg);
          if (customerId && phaseId) {
            navigate(`/customers/${customerId}/phases/${phaseId}`);
          } else {
            navigate(`/customers/${customerId}`);
          }
        }
      }
    },
    onError: (error: any) => {
      const msg = error.response?.data?.error?.message || 'Connection test failed';
      setTestResult({
        success: false,
        message: msg,
        timestamp: new Date().toISOString()
      });
      if (onFailure) {
        onFailure(msg);
      } else {
        toast.error(msg);
        if (customerId && phaseId) {
          navigate(`/customers/${customerId}/phases/${phaseId}`);
        } else {
          navigate(`/customers/${customerId}`);
        }
      }
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    saveCredentialsMutation.mutate(formData);
  };

  const handleTestConnection = () => {
    // Pass current URL as returnUrl so backend redirects back here
    const returnUrl = window.location.pathname + window.location.search;
    testConnectionMutation.mutate({
      ...formData,
      returnUrl
    });
  };

  const handleInputChange = (field: keyof SharePointCredentials, value: string) => {
    setFormData((prev: SharePointCredentials) => ({ ...prev, [field]: value }));
    setTestResult(null);
  };

  // Delete SharePoint credentials mutation
  const deleteCredentialsMutation = useMutation({
    mutationFn: () => apiClient.deleteCustomerSharePointCredentials(customerId),
    onSuccess: () => {
      toast.success('SharePoint credentials deleted successfully');
      window.location.reload();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to delete SharePoint credentials');
    }
  });

  const handleDeleteCredentials = () => {
    if (window.confirm('Are you sure you want to delete SharePoint credentials for this customer?')) {
      deleteCredentialsMutation.mutate();
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="loading-spinner w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className={isEmbedded ? "max-w-4xl mx-auto p-6" : "bg-white rounded-lg shadow-lg p-6 max-w-2xl mx-auto"}>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          {!isEmbedded && <CloudIcon className="w-8 h-8 text-blue-600" />}
          <div>
            <h2 className="text-2xl font-bold text-gray-900">SharePoint Credentials</h2>
            <p className="text-gray-600">Manage SharePoint credentials for this customer</p>
          </div>
        </div>
        {!isEmbedded && onClose && (
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XCircleIcon className="w-6 h-6" />
          </button>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <label className="form-label">SharePoint Site URL *</label>
            <input
              type="url"
              value={formData.siteUrl}
              onChange={(e) => handleInputChange('siteUrl', e.target.value)}
              className="form-input"
              placeholder="https://{{yourcompany}}.sharepoint.com/sites/{{yoursite}}"
              required
            />
          </div>
          <div>
            <label className="form-label">Client ID *</label>
            <input
              type="text"
              value={formData.clientId}
              onChange={(e) => handleInputChange('clientId', e.target.value)}
              className="form-input"
              placeholder="Enter SharePoint client ID"
              required
            />
          </div>
          <div>
            <label className="form-label">Client Secret</label>
            <input
              type="password"
              value={formData.clientSecret || ''}
              onChange={(e) => handleInputChange('clientSecret', e.target.value)}
              className="form-input"
              placeholder="Enter Client Secret (for App-only access)"
            />
            <p className="text-xs text-gray-500 mt-1">If provided, enables App-only access for all users.</p>
          </div>
          <div>
            <label className="form-label">Tenant ID *</label>
            <input
              type="text"
              value={formData.tenantId}
              onChange={(e) => handleInputChange('tenantId', e.target.value)}
              className="form-input"
              placeholder="Enter SharePoint tenant ID"
              required
            />
          </div>
        </div>

        {/* Certificate Information */}
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
          <h2 className="text-lg font-semibold text-blue-900 mb-4">How to Setup SharePoint OAuth?</h2>
          <div className="prose prose-blue max-w-none text-sm text-blue-800">
            <ol className="list-decimal pl-5 space-y-2">
              <li>Log in to the <strong>Azure Portal</strong> (Entra ID).</li>
              <li>Navigate to <strong>App registrations</strong> and create a new registration.</li>
              <li>Fill in the name (e.g., "Migration App").</li>
              <li>
                Click on <strong>Certificates & secrets</strong> &rarr; <strong>Certificates</strong> &rarr; <strong>Upload certificate</strong>.
                <div className="mt-2">
                  <a
                    href={`${getApiBaseUrl()}/credentials/certificate?type=sharepoint`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
                    Download Certificate
                  </a>
                </div>
              </li>
              <li>Go to <strong>API permissions</strong> &rarr; <strong>Add a permission</strong> &rarr; <strong>SharePoint</strong>.</li>
              <li>Select <strong>Application permissions</strong> and check <strong>Sites.ReadWrite.All</strong> (and <strong>User.Read.All</strong> if needed).</li>
              <li>Click <strong>Grant admin consent</strong>.</li>
              <li>Copy the <strong>Application (client) ID</strong> and <strong>Directory (tenant) ID</strong> from the Overview page and paste them below.</li>
              <li>Add <strong>{typeof window !== 'undefined' ? `${window.location.origin.replace(/\/$/, '')}/api/sharepoint/oauth/callback` : 'https://your-domain.com/api/sharepoint/oauth/callback'}</strong> as redirect URL for this application in Entra</li>
            </ol>
          </div>
        </div>




        {/* Test Result */}
        {testResult && (
          <div className={`p-4 rounded-md ${testResult.success
            ? 'bg-green-50 border border-green-200'
            : 'bg-red-50 border border-red-200'
            }`}>
            <div className="flex items-center">
              {testResult.success ? (
                <CheckCircleIcon className="w-5 h-5 text-green-400 mr-2" />
              ) : (
                <XCircleIcon className="w-5 h-5 text-red-400 mr-2" />
              )}
              <div>
                <p className={`text-sm font-medium ${testResult.success ? 'text-green-800' : 'text-red-800'
                  }`}>
                  {testResult.success ? 'Connection Test Successful' : 'Connection Test Failed'}
                </p>
                <p className={`text-sm ${testResult.success ? 'text-green-700' : 'text-red-700'
                  }`}>
                  {testResult.message}
                </p>
                {testResult.timestamp && (
                  <p className={`text-xs ${testResult.success ? 'text-green-600' : 'text-red-600'
                    }`}>
                    {new Date(testResult.timestamp).toLocaleString()}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-between pt-6 border-t border-gray-200">
          <div className="flex space-x-3">
            <button
              type="submit"
              disabled={saveCredentialsMutation.isPending}
              className="btn-primary"
            >
              {saveCredentialsMutation.isPending ? 'Saving...' : 'Save SharePoint Credentials'}
            </button>
            <button
              type="button"
              onClick={handleTestConnection}
              disabled={testConnectionMutation.isPending}
              className="btn-secondary"
            >
              {testConnectionMutation.isPending ? 'Testing...' : 'Test Connection'}
            </button>
          </div>
          <div className="flex space-x-3">
            {existingCredentials && (
              <button
                type="button"
                onClick={handleDeleteCredentials}
                className="text-red-600 hover:text-red-800 text-sm"
              >
                Delete Credentials
              </button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default CustomerSharePointCredentialsForm;